/**
 * Sistema Centralizado de Tratamento de Erros
 * 
 * Este módulo fornece funções para tratar erros de forma consistente
 * em toda a aplicação, com mensagens amigáveis ao usuário.
 */

import { toast } from 'react-toastify';

/**
 * Tipos de erro conhecidos
 */
export const ErrorTypes = {
  NETWORK: 'network',
  STOCK: 'stock',
  AUTH: 'auth',
  VALIDATION: 'validation',
  NOT_FOUND: 'not_found',
  SERVER: 'server',
  UNKNOWN: 'unknown',
};

/**
 * Mensagens amigáveis para cada tipo de erro
 */
const ERROR_MESSAGES = {
  [ErrorTypes.NETWORK]: 'Erro de conexão. Verifique sua internet e tente novamente.',
  [ErrorTypes.STOCK]: 'Produto sem estoque no momento. Por favor, escolha outro produto ou entre em contato conosco.',
  [ErrorTypes.AUTH]: 'Sua sessão expirou. Por favor, faça login novamente.',
  [ErrorTypes.VALIDATION]: 'Por favor, verifique os dados informados e tente novamente.',
  [ErrorTypes.NOT_FOUND]: 'Item não encontrado. Por favor, tente novamente.',
  [ErrorTypes.SERVER]: 'Erro no servidor. Tente novamente em alguns instantes.',
  [ErrorTypes.UNKNOWN]: 'Ocorreu um erro inesperado. Tente novamente mais tarde.',
};

/**
 * Detecta o tipo de erro baseado na mensagem ou código
 */
const detectErrorType = (error) => {
  if (!error) return ErrorTypes.UNKNOWN;

  const errorMessage = error.message?.toLowerCase() || '';
  const errorCode = error.code || error.status || '';

  // Erro de rede
  if (
    errorMessage.includes('network') ||
    errorMessage.includes('fetch') ||
    errorMessage.includes('conexão') ||
    errorMessage.includes('connection') ||
    errorCode === 'NETWORK_ERROR'
  ) {
    return ErrorTypes.NETWORK;
  }

  // Erro de estoque
  if (
    errorMessage.includes('estoque') ||
    errorMessage.includes('stock') ||
    errorMessage.includes('disponível') ||
    errorMessage.includes('available')
  ) {
    return ErrorTypes.STOCK;
  }

  // Erro de autenticação
  if (
    errorMessage.includes('token') ||
    errorMessage.includes('auth') ||
    errorMessage.includes('sessão') ||
    errorMessage.includes('session') ||
    errorMessage.includes('unauthorized') ||
    errorCode === 401
  ) {
    return ErrorTypes.AUTH;
  }

  // Erro de validação
  if (
    errorMessage.includes('validation') ||
    errorMessage.includes('validação') ||
    errorMessage.includes('invalid') ||
    errorMessage.includes('inválido') ||
    errorCode === 400
  ) {
    return ErrorTypes.VALIDATION;
  }

  // Erro não encontrado
  if (
    errorMessage.includes('not found') ||
    errorMessage.includes('não encontrado') ||
    errorCode === 404
  ) {
    return ErrorTypes.NOT_FOUND;
  }

  // Erro do servidor
  if (errorCode >= 500) {
    return ErrorTypes.SERVER;
  }

  return ErrorTypes.UNKNOWN;
};

/**
 * Obtém mensagem amigável para o usuário
 */
const getUserFriendlyMessage = (error, customMessage = null) => {
  if (customMessage) return customMessage;

  const errorType = detectErrorType(error);
  const baseMessage = ERROR_MESSAGES[errorType] || ERROR_MESSAGES[ErrorTypes.UNKNOWN];

  // Se o erro tem uma mensagem específica e útil, usa ela
  if (error?.message && error.message !== error.message.toLowerCase()) {
    // Verifica se a mensagem não é muito técnica
    const technicalTerms = ['error', 'exception', 'failed', 'undefined', 'null'];
    const isTechnical = technicalTerms.some(term => 
      error.message.toLowerCase().includes(term)
    );

    if (!isTechnical && error.message.length < 100) {
      return error.message;
    }
  }

  return baseMessage;
};

/**
 * Loga o erro para desenvolvimento (apenas em modo dev)
 */
const logError = (error, context = '') => {
  if (process.env.NODE_ENV === 'development') {
    const contextInfo = context ? `[${context}]` : '';
    console.error(`${contextInfo} Erro:`, {
      message: error?.message,
      code: error?.code || error?.status,
      stack: error?.stack,
      fullError: error,
    });
  }
};

/**
 * Trata um erro e exibe mensagem amigável ao usuário
 * 
 * @param {Error|Object} error - O erro ocorrido
 * @param {Object} options - Opções de tratamento
 * @param {string} options.context - Contexto onde o erro ocorreu (para logs)
 * @param {string} options.customMessage - Mensagem customizada para o usuário
 * @param {boolean} options.showToast - Se deve exibir toast (padrão: true)
 * @param {string} options.toastType - Tipo do toast ('error', 'warning', 'info')
 * 
 * @returns {string} Mensagem amigável para o usuário
 */
export const handleError = (error, options = {}) => {
  const {
    context = '',
    customMessage = null,
    showToast = true,
    toastType = 'error',
  } = options;

  // Loga o erro (apenas em desenvolvimento)
  logError(error, context);

  // Obtém mensagem amigável
  const userMessage = getUserFriendlyMessage(error, customMessage);

  // Exibe toast se solicitado
  if (showToast) {
    toast[toastType](userMessage, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }

  return userMessage;
};

/**
 * Trata erros de API de forma específica
 * 
 * @param {Response} response - Resposta da API
 * @param {Object} options - Opções de tratamento
 * 
 * @returns {Promise<string>} Mensagem de erro ou null se não houver erro
 */
export const handleApiError = async (response, options = {}) => {
  if (response.ok) return null;

  let errorData;
  try {
    errorData = await response.json();
  } catch {
    errorData = { message: 'Erro ao processar resposta do servidor' };
  }

  const firstFieldError = Array.isArray(errorData.errors)
    ? errorData.errors[0]?.message || errorData.errors[0]
    : null;

  const error = {
    message:
      firstFieldError ||
      errorData.message ||
      errorData.error ||
      'Erro ao processar requisição',
    code: response.status,
    status: response.status,
    errors: errorData.errors,
  };

  return handleError(error, {
    ...options,
    context: options.context || 'API',
  });
};

/**
 * Wrapper para funções assíncronas com tratamento de erro automático
 * 
 * @param {Function} asyncFn - Função assíncrona
 * @param {Object} options - Opções de tratamento
 * 
 * @returns {Promise} Promise que resolve com o resultado ou rejeita com erro tratado
 */
export const withErrorHandling = (asyncFn, options = {}) => {
  return async (...args) => {
    try {
      return await asyncFn(...args);
    } catch (error) {
      const message = handleError(error, options);
      throw new Error(message);
    }
  };
};

/**
 * Cria um handler de erro específico para um contexto
 * 
 * @param {string} context - Contexto onde será usado
 * 
 * @returns {Function} Função handleError configurada para o contexto
 */
export const createErrorHandler = (context) => {
  return (error, options = {}) => {
    return handleError(error, {
      ...options,
      context: options.context || context,
    });
  };
};

